/**
 * Conexiones de la cuenta (Facebook/Instagram vía Facebook Login for Business, WhatsApp Business vía Embedded
 * Signup) y publicaciones que no se pudieron borrar. Mismo patrón que `products`: se muestra lo último conocido
 * mientras se refresca.
 */
import { defineStore } from "pinia";
import { computed, shallowRef } from "vue";

import { api } from "@/lib/api";
import type {
  ConnectionsResponse,
  Destination,
  MetaConnection,
  WhatsAppConnection,
  WhatsAppSignupConfig,
} from "@/lib/contracts";

export const useConnectionsStore = defineStore("connections", () => {
  const data = shallowRef<ConnectionsResponse | null>(null);

  const meta = computed<MetaConnection | null>(() => data.value?.meta ?? null);
  const orphans = computed(() => data.value?.orphans ?? []);
  const whatsapp = computed<WhatsAppConnection | null>(() => data.value?.whatsapp ?? null);
  const whatsappSignup = computed<WhatsAppSignupConfig | null>(() => data.value?.whatsapp_signup ?? null);
  /** Avisos para la biblioteca: hay que reconectar o hay publicaciones por borrar a mano. */
  const needsAttention = computed(
    () =>
      meta.value?.status === "needs_reauth" || whatsapp.value?.status === "needs_reauth" || orphans.value.length > 0,
  );

  async function refresh(): Promise<ConnectionsResponse> {
    const res = await api.getConnections();
    // Un backend sin /connections (o una respuesta no JSON) no debe romper la biblioteca.
    data.value = {
      meta: res.meta ?? null,
      meta_login_available: res.meta_login_available ?? false,
      destinations: res.destinations ?? {},
      orphans: Array.isArray(res.orphans) ? res.orphans : [],
      whatsapp: res.whatsapp ?? null,
      whatsapp_signup: res.whatsapp_signup ?? null,
    };
    return data.value;
  }

  function setMeta(next: MetaConnection): void {
    if (data.value) data.value = { ...data.value, meta: next };
  }

  function setWhatsApp(next: WhatsAppConnection): void {
    if (data.value) data.value = { ...data.value, whatsapp: next };
  }

  function dropOrphan(productId: string, destination: Destination): void {
    if (!data.value) return;
    data.value = {
      ...data.value,
      orphans: data.value.orphans.filter((o) => !(o.product_id === productId && o.destination === destination)),
    };
  }

  function reset(): void {
    data.value = null;
  }

  return {
    data,
    meta,
    orphans,
    whatsapp,
    whatsappSignup,
    needsAttention,
    refresh,
    setMeta,
    setWhatsApp,
    dropOrphan,
    reset,
  };
});
